/**
 * Static parser for `browser_run_code` (fast-drive) Playwright source.
 *
 * Fast-drive agents act only through `browser_run_code` — driver-side Playwright
 * code. Downstream observability (timeline / SSE / coverage) keys on discrete
 * actions, so a `run_code` block that performs N interactions collapses to one
 * opaque entry. A Playwright action returns nothing about what it touched
 * (`locator.click()` → `Promise<void>`), so the only way to recover the
 * interaction list is to read the **code**.
 *
 * This module statically parses the code (via ts-morph, already a repo dep) and
 * recovers a best-effort list of interactions, mapping terminal Playwright calls
 * onto our action vocabulary. It reports **intent, not runtime truth**: it
 * descends into block bodies (loops/conditionals/try/`Promise.all`) emitting each
 * action **once** (never unrolling a loop or picking a runtime branch), and it
 * tracks locator **aliases** — `const row = page.getByRole(…)` so a later
 * `row.click()` still classifies — but it never resolves runtime *values* or
 * follows data flow beyond a direct locator assignment. Anything it can't
 * classify is silently skipped; a block with zero recognized actions reports
 * `fullFallback`. Caching/recording is NOT a concern here — this feeds
 * observability only.
 */
import { Project, Node, type CallExpression, type Statement, type SourceFile } from 'ts-morph';

/** Action vocabulary the parser emits — aligned with `BrowserActionType`. */
export type ParsedActionType =
  | 'click'
  | 'check'
  | 'uncheck'
  | 'type'
  | 'select'
  | 'press-key'
  | 'hover'
  | 'drag'
  | 'scroll'
  | 'move'
  | 'file-upload'
  | 'navigate'
  | 'wait';

export interface ParsedInteraction {
  /** 0-based order within the run_code block. */
  index: number;
  actionType: ParsedActionType;
  /** Synthetic IBrowserClient-style method name (drives the timeline gate + metadata.method). */
  method: string;
  /** Rendered locator chain, e.g. `getByRole("button", { name: "Save" })`. */
  elementDescription?: string;
  /** Literal-or-token value for type/select (e.g. `"hello"`, `vars.x`, `await macro("EMAIL")`). */
  value?: string;
  /** Key for press-key. */
  key?: string;
  /** URL for navigate(goto). */
  url?: string;
  /** File path token for upload. */
  filePath?: string;
  /** Coordinates for `page.mouse.click(x, y)`. */
  coordinates?: { x: number; y: number };
  /** True for `.dblclick()`. */
  doubleClick?: boolean;
  /** Exact source slice for the statement (for metadata.code / debugging). */
  source: string;
}

export interface RunCodeParseResult {
  interactions: ParsedInteraction[];
  /**
   * True when parsing threw or no recognized action was found — the caller keeps
   * today's behavior (no synthetic entries) for the block.
   */
  fullFallback: boolean;
}

export interface ParsedInteractionProvenance {
  /** Source text for the containing statement, not just the terminal call. */
  statementSource: string;
  /** 0-based source offset for `statementSource` within the parsed run_code body. */
  start: number;
  /** End source offset for `statementSource` within the parsed run_code body. */
  end: number;
  /** True when the interaction was found under a branch/loop/try/fan-out construct. */
  underControlFlow: boolean;
}

export interface StrictParsedInteraction extends ParsedInteraction {
  provenance: ParsedInteractionProvenance;
}

export interface RunCodeStrictParseResult extends Omit<RunCodeParseResult, 'interactions'> {
  interactions: StrictParsedInteraction[];
  /** True when static intent may diverge from runtime truth due to control flow. */
  hasControlFlow: boolean;
  /** Count of statements with page-touching calls the best-effort parser skipped. */
  unclassifiedCount: number;
}

/** Bodies larger than this skip parsing (ts-morph parses synchronously). */
const MAX_PARSE_CODE_LENGTH = 16_000;
/** Monotonic counter for per-call source-file names (parse isolation). */
let _fileCounter = 0;

// Lazily-created, reused in-memory project (parse is not hot-loop frequency).
let _project: Project | null = null;
function getProject(): Project {
  if (!_project) {
    _project = new Project({
      useInMemoryFileSystem: true,
      compilerOptions: { target: 99 /* ESNext */, module: 99 /* ESNext */ },
    });
  }
  return _project;
}

/** Locator-terminal method → action type (the reverse of our ref-tool mapping). */
const LOCATOR_ACTIONS: Record<string, ParsedActionType> = {
  click: 'click',
  dblclick: 'click',
  check: 'check',
  uncheck: 'uncheck',
  fill: 'type',
  type: 'type',
  pressSequentially: 'type',
  selectOption: 'select',
  press: 'press-key',
  hover: 'hover',
  dragTo: 'drag',
  setInputFiles: 'file-upload',
  // NOTE: `scrollIntoViewIfNeeded` is intentionally NOT mapped — it's a
  // pre-action visibility helper (reveal-before-act) agents call before most
  // clicks/fills, not a user-directed scroll, so pinning it as a `scroll`
  // interaction is misleading noise. Real scrolls (`page.mouse.wheel`) surface.
  waitFor: 'wait',
};

/** `page.<method>(...)` terminal → action type. */
const PAGE_ACTIONS: Record<string, ParsedActionType> = {
  goto: 'navigate',
  goBack: 'navigate',
  waitForURL: 'wait',
  waitForTimeout: 'wait',
  waitForSelector: 'wait',
  waitForLoadState: 'wait',
  waitForFunction: 'wait',
  waitForEvent: 'wait',
  waitForResponse: 'wait',
  waitForRequest: 'wait',
};

const INTERACTION_VERBS = new Set([
  ...Object.keys(LOCATOR_ACTIONS),
  ...Object.keys(PAGE_ACTIONS),
  'check',
  'uncheck',
  'setChecked',
  'tap',
  'focus',
  'clear',
  'dispatchEvent',
  'selectText',
]);

const LOCATOR_BUILDER_METHODS = new Set([
  'locator',
  'getByRole',
  'getByText',
  'getByLabel',
  'getByPlaceholder',
  'getByAltText',
  'getByTitle',
  'getByTestId',
  'frameLocator',
  'filter',
  'first',
  'last',
  'nth',
  'and',
  'or',
  'describe',
  'contentFrame',
  'owner',
]);

const BENIGN_PAGE_READ_METHODS = new Set([
  'all',
  'allInnerTexts',
  'allTextContents',
  'ariaSnapshot',
  'boundingBox',
  'content',
  'context',
  'count',
  'frames',
  'getAttribute',
  'innerHTML',
  'innerText',
  'inputValue',
  'isChecked',
  'isClosed',
  'isDisabled',
  'isEditable',
  'isEnabled',
  'isHidden',
  'isVisible',
  'mainFrame',
  'screenshot',
  'textContent',
  'title',
  'url',
  'video',
  'viewportSize',
]);

/** Render a function-argument node as a literal value or its source token. */
function renderArg(node: Node | undefined): string | undefined {
  if (!node) return undefined;
  if (Node.isStringLiteral(node) || Node.isNoSubstitutionTemplateLiteral(node)) {
    return node.getLiteralText();
  }
  return node.getText();
}

/** Strip a leading `page.` so the locator chain reads as the element description. */
function stripPagePrefix(text: string): string {
  return text.replace(/^page\./, '');
}

/** Unwrap `await (...)` / parentheses down to the underlying expression. */
function unwrap(expr: Node): Node {
  let cur: Node = expr;
  while (Node.isAwaitExpression(cur) || Node.isParenthesizedExpression(cur)) {
    cur = cur.getExpression();
  }
  return cur;
}

/** The leftmost identifier of a member/call chain, e.g. `page` in `page.getBy…().click`. */
function leftmostIdentifier(expr: Node): string | null {
  let cur: Node = expr;
  // Descend through property-access and call chains to the root identifier.
  // CallExpression.getExpression() is the callee; PropertyAccess.getExpression()
  // is the object. Both move us leftward.
  for (let guard = 0; guard < 100; guard++) {
    if (Node.isPropertyAccessExpression(cur) || Node.isCallExpression(cur)) {
      cur = cur.getExpression();
      continue;
    }
    if (Node.isNonNullExpression(cur) || Node.isParenthesizedExpression(cur)) {
      cur = cur.getExpression();
      continue;
    }
    break;
  }
  return Node.isIdentifier(cur) ? cur.getText() : null;
}

/** Is `expr` exactly `page.mouse` / `page.keyboard`? Returns the sub-namespace name. */
function pageNamespace(expr: Node): 'mouse' | 'keyboard' | null {
  if (!Node.isPropertyAccessExpression(expr)) return null;
  const obj = expr.getExpression();
  if (!Node.isIdentifier(obj) || obj.getText() !== 'page') return null;
  const name = expr.getName();
  return name === 'mouse' || name === 'keyboard' ? name : null;
}

/** Variable name → rendered locator source (page-prefix stripped, alias substituted). */
type AliasMap = Map<string, string>;

/** Unwrap parentheses / non-null only — NOT await (an awaited value is resolved, not a builder). */
function unwrapParens(expr: Node): Node {
  let cur: Node = expr;
  while (Node.isParenthesizedExpression(cur) || Node.isNonNullExpression(cur)) {
    cur = cur.getExpression();
  }
  return cur;
}

/**
 * Render a page- or alias-rooted locator chain as an element description: strip a
 * leading `page.`, or substitute a known alias back to its stored source so the
 * description is fully-qualified. Returns null when the chain roots at neither
 * `page` nor a known alias.
 */
function renderLocatorSource(expr: Node, aliases: AliasMap): string | null {
  const core = unwrapParens(expr);
  const root = leftmostIdentifier(core);
  if (root === 'page') return stripPagePrefix(core.getText());
  if (root && aliases.has(root)) {
    const text = core.getText();
    const aliasSource = aliases.get(root)!;
    // The alias is always the leftmost token; splice its stored source in. If the
    // text doesn't start with the bare root (rare: parenthesized root), fall back
    // to the raw text — still a useful description.
    return text.startsWith(root) ? aliasSource + text.slice(root.length) : text;
  }
  return null;
}

function setCheckedAction(args: Node[]): 'check' | 'uncheck' | null {
  const arg = args[0] ? unwrapParens(args[0]).getText() : '';
  if (arg === 'true') return 'check';
  if (arg === 'false') return 'uncheck';
  return null;
}

/**
 * Record `const/let X = <page-or-alias-rooted locator chain>` so a later
 * `X.click()` classifies. Skips destructuring, awaited initializers (resolved
 * values, e.g. `.count()`), and chains that terminate in an action (those are
 * actions, not locator builders). Last-write-wins on reassignment (source order).
 */
function recordAlias(
  nameNode: Node | undefined,
  initExpr: Node | undefined,
  aliases: AliasMap
): boolean {
  if (!nameNode || !initExpr) return false;
  if (!Node.isIdentifier(nameNode)) return false; // skip destructuring patterns
  if (Node.isAwaitExpression(initExpr)) return false; // resolved value, not a locator builder
  const core = unwrapParens(initExpr);
  const root = leftmostIdentifier(core);
  if (root !== 'page' && !(root && aliases.has(root))) return false;
  if (Node.isCallExpression(core)) {
    const callee = core.getExpression();
    if (Node.isPropertyAccessExpression(callee)) {
      const m = callee.getName();
      if (LOCATOR_ACTIONS[m] || PAGE_ACTIONS[m]) return false; // terminates in an action → not a builder
    }
  }
  const rendered = renderLocatorSource(core, aliases);
  if (rendered === null) return false;
  aliases.set(nameNode.getText(), rendered);
  return true;
}

/**
 * Classify an (already-unwrapped) `CallExpression` into an interaction, or null
 * if it isn't a recognized action rooted at `page` or a known locator alias.
 */
function classifyCallExpression(
  expr: CallExpression,
  aliases: AliasMap
): Omit<ParsedInteraction, 'index'> | null {
  const source = expr.getText().trim();
  const callee = expr.getExpression();
  if (!Node.isPropertyAccessExpression(callee)) return null;

  const method = callee.getName();
  const object = callee.getExpression();
  const args = expr.getArguments();

  // page.mouse.* / page.keyboard.* specials
  const ns = pageNamespace(object);
  if (ns === 'mouse') {
    if (method === 'click' || method === 'dblclick') {
      const x = Number(renderArg(args[0]));
      const y = Number(renderArg(args[1]));
      return {
        actionType: 'click',
        method: 'clickAtCoordinates',
        coordinates: Number.isFinite(x) && Number.isFinite(y) ? { x, y } : undefined,
        doubleClick: method === 'dblclick' || undefined,
        source,
      };
    }
    if (method === 'wheel') return { actionType: 'scroll', method: 'scroll', source };
    // down/move/up: surfaced as discrete `move` interactions (manual drags are
    // built from these). Emit one per call rather than reconstructing a paced
    // drag — observability favors showing the agent acted over perfect grouping.
    if (method === 'move' || method === 'down' || method === 'up') {
      const x = Number(renderArg(args[0]));
      const y = Number(renderArg(args[1]));
      return {
        actionType: 'move',
        method: `mouse${method.charAt(0).toUpperCase()}${method.slice(1)}`,
        coordinates: Number.isFinite(x) && Number.isFinite(y) ? { x, y } : undefined,
        source,
      };
    }
    return null;
  }
  if (ns === 'keyboard') {
    if (method === 'press')
      return { actionType: 'press-key', method: 'pressKey', key: renderArg(args[0]), source };
    if (method === 'type')
      return { actionType: 'type', method: 'type', value: renderArg(args[0]), source };
    return null;
  }

  // Must be rooted at `page` OR a known locator alias.
  const root = leftmostIdentifier(object);
  const isPageRoot = root === 'page';
  if (!isPageRoot && !(root && aliases.has(root))) return null;

  // page.<action>(...) (navigate / waits) — only the bare `page` identifier.
  if (Node.isIdentifier(object) && object.getText() === 'page') {
    const pageAction = PAGE_ACTIONS[method];
    if (!pageAction) return null;
    if (method === 'goto')
      return { actionType: 'navigate', method: 'navigate', url: renderArg(args[0]), source };
    if (method === 'goBack') return { actionType: 'navigate', method: 'navigateBack', source };
    return { actionType: 'wait', method: 'waitFor', source };
  }

  // Locator chain: (page|alias).getBy…/locator(...).<refinements>.<action>(...)
  const action = method === 'setChecked' ? setCheckedAction(args) : LOCATOR_ACTIONS[method];
  if (!action) return null; // reads (.count/.innerText/.evaluate/…) fall through here → skipped
  const elementDescription = isPageRoot
    ? stripPagePrefix(object.getText())
    : (renderLocatorSource(object, aliases) ?? object.getText());
  // `method` is supplied by each switch arm below (the canonical synthetic
  // method name), not the raw Playwright verb — so it's omitted from `base`.
  const base: Omit<ParsedInteraction, 'index' | 'method'> = {
    actionType: action,
    elementDescription,
    source,
  };

  switch (action) {
    case 'click':
      return { ...base, method: 'click', doubleClick: method === 'dblclick' || undefined };
    case 'check':
      return { ...base, method: 'check' };
    case 'uncheck':
      return { ...base, method: 'uncheck' };
    case 'type':
      return { ...base, method: 'type', value: renderArg(args[0]) };
    case 'select':
      return { ...base, method: 'selectOption', value: renderArg(args[0]) };
    case 'press-key':
      return { ...base, method: 'pressKey', key: renderArg(args[0]) };
    case 'file-upload':
      return { ...base, method: 'fileUpload', filePath: renderArg(args[0]) };
    case 'hover':
      return { ...base, method: 'hover' };
    case 'drag':
      return { ...base, method: 'drag' };
    case 'scroll':
      return { ...base, method: 'scroll' };
    case 'wait':
      return { ...base, method: 'waitFor' };
    default:
      return null;
  }
}

/** Recursion cap for the statement walker — a backstop against pathological nesting. */
const MAX_WALK_DEPTH = 50;

interface StrictParseStats {
  hasControlFlow: boolean;
  unclassifiedCount: number;
}

type InteractionSink =
  | { strict: false; interactions: ParsedInteraction[] }
  | { strict: true; interactions: StrictParsedInteraction[]; stats: StrictParseStats };

interface WalkContext {
  depth: number;
  underControlFlow: boolean;
}

interface StatementWalkContext extends WalkContext {
  statement: Statement;
}

function markControlFlow(sink: InteractionSink): void {
  if (sink.strict) sink.stats.hasControlFlow = true;
}

function incrementUnclassified(sink: InteractionSink, shouldCount: boolean): void {
  if (sink.strict && shouldCount) sink.stats.unclassifiedCount += 1;
}

function pushInteraction(
  parsed: Omit<ParsedInteraction, 'index'>,
  sink: InteractionSink,
  ctx: StatementWalkContext
): void {
  if (sink.strict) {
    const sourceNode = ctx.statement;
    const interaction: StrictParsedInteraction = {
      ...parsed,
      index: sink.interactions.length,
      provenance: {
        statementSource: sourceNode.getText(),
        start: sourceNode.getStart(),
        end: sourceNode.getEnd(),
        underControlFlow: ctx.underControlFlow,
      },
    };
    sink.interactions.push(interaction);
    return;
  }

  sink.interactions.push({ ...parsed, index: sink.interactions.length });
}

function isPotentialUnclassifiedPageCall(expr: CallExpression, aliases: AliasMap): boolean {
  const callee = expr.getExpression();
  if (!Node.isPropertyAccessExpression(callee)) return false;
  const method = callee.getName();
  const object = callee.getExpression();
  const root = leftmostIdentifier(object);
  const rootedAtKnownPageObject = root === 'page' || (root !== null && aliases.has(root));

  // A known action verb on an unknown root might still be a locator variable the
  // alias tracker could not prove, so strict mode reports the skipped statement.
  if (!rootedAtKnownPageObject) return INTERACTION_VERBS.has(method);
  if (LOCATOR_ACTIONS[method] || PAGE_ACTIONS[method]) return false;
  if (LOCATOR_BUILDER_METHODS.has(method) || BENIGN_PAGE_READ_METHODS.has(method)) return false;
  return true;
}

function expressionHasControlFlowWithInteraction(expr: Node, aliases: AliasMap): boolean {
  function hasInteractionIn(node: Node): boolean {
    return expressionCouldHideInteraction(node, aliases);
  }

  if (Node.isConditionalExpression(expr)) {
    return (
      hasInteractionIn(expr.getCondition()) ||
      hasInteractionIn(expr.getWhenTrue()) ||
      hasInteractionIn(expr.getWhenFalse())
    );
  }
  if (Node.isBinaryExpression(expr)) {
    const op = expr.getOperatorToken().getText();
    if (op === '&&' || op === '||')
      return hasInteractionIn(expr.getLeft()) || hasInteractionIn(expr.getRight());
  }

  let found = false;
  expr.forEachDescendant((node, traversal) => {
    if (found) {
      traversal.stop();
      return;
    }
    if (Node.isConditionalExpression(node)) {
      if (
        hasInteractionIn(node.getCondition()) ||
        hasInteractionIn(node.getWhenTrue()) ||
        hasInteractionIn(node.getWhenFalse())
      ) {
        found = true;
        traversal.stop();
      }
      return;
    }
    if (!Node.isBinaryExpression(node)) return;
    const op = node.getOperatorToken().getText();
    if (
      (op === '&&' || op === '||') &&
      (hasInteractionIn(node.getLeft()) || hasInteractionIn(node.getRight()))
    ) {
      found = true;
      traversal.stop();
    }
  });
  return found;
}

function expressionCouldHideInteraction(expr: Node, aliases: AliasMap): boolean {
  if (Node.isCallExpression(expr)) {
    if (classifyCallExpression(expr, aliases) || isPotentialUnclassifiedPageCall(expr, aliases))
      return true;
  }

  let found = false;
  expr.forEachDescendant((node, traversal) => {
    if (found) {
      traversal.stop();
      return;
    }
    if (!Node.isCallExpression(node)) return;
    if (classifyCallExpression(node, aliases) || isPotentialUnclassifiedPageCall(node, aliases)) {
      found = true;
      traversal.stop();
    }
  });
  return found;
}

/**
 * Classify an expression's terminal action(s) and push them in source order.
 * Unwraps `await`/parens, fans out `Promise.all/allSettled/race([...])` array
 * elements, and descends one level into `.map/.forEach/.filter/.flatMap`
 * callbacks (registering the callback param as an opaque locator alias).
 */
function extractActionsFromExpression(
  expr: Node,
  aliases: AliasMap,
  sink: InteractionSink,
  ctx: StatementWalkContext
): boolean {
  if (ctx.depth > MAX_WALK_DEPTH) {
    incrementUnclassified(sink, true);
    return false;
  }
  if (expressionHasControlFlowWithInteraction(expr, aliases)) markControlFlow(sink);
  const core = unwrap(expr);
  if (!Node.isCallExpression(core)) return expressionCouldHideInteraction(core, aliases);

  const callee = core.getExpression();
  if (Node.isPropertyAccessExpression(callee)) {
    const m = callee.getName();

    // Promise.all([...]) / allSettled / race → fan out the awaited actions.
    if (
      (m === 'all' || m === 'allSettled' || m === 'race') &&
      leftmostIdentifier(callee) === 'Promise'
    ) {
      markControlFlow(sink);
      let couldHide = false;
      const childCtx: StatementWalkContext = {
        ...ctx,
        depth: ctx.depth + 1,
        underControlFlow: true,
      };
      const first = core.getArguments()[0];
      if (first && Node.isArrayLiteralExpression(first)) {
        for (const el of first.getElements()) {
          couldHide = extractActionsFromExpression(el, aliases, sink, childCtx) || couldHide;
        }
      } else if (first) {
        couldHide = extractActionsFromExpression(first, aliases, sink, childCtx); // e.g. Promise.all(items.map(…))
      }
      return couldHide;
    }

    // Array-iteration callbacks: descend the body once (intent, not unrolled).
    if (m === 'map' || m === 'forEach' || m === 'filter' || m === 'flatMap') {
      const cb = core.getArguments()[0];
      if (cb && (Node.isArrowFunction(cb) || Node.isFunctionExpression(cb))) {
        markControlFlow(sink);
        const param = cb.getParameters()[0]?.getNameNode();
        if (param && Node.isIdentifier(param)) aliases.set(param.getText(), param.getText());
        const body = cb.getBody();
        const childCtx: StatementWalkContext = {
          ...ctx,
          depth: ctx.depth + 1,
          underControlFlow: true,
        };
        if (Node.isBlock(body)) {
          walkStatements(body.getStatements(), aliases, sink, childCtx);
          return false;
        }
        return extractActionsFromExpression(body, aliases, sink, childCtx);
      }
      if (cb && sink.strict) {
        // Opaque named callbacks can hide per-item locator work; report
        // uncertainty instead of pretending the static body was empty.
        markControlFlow(sink);
        return true;
      }
    }
  }

  const parsed = classifyCallExpression(core, aliases);
  if (parsed) {
    pushInteraction(parsed, sink, ctx);
    return false;
  }
  return expressionCouldHideInteraction(core, aliases);
}

/** Register a `for..of` / `for..in` loop binding as an opaque alias (description = its own text). */
function recordLoopBinding(init: Node | undefined, aliases: AliasMap): void {
  if (init && Node.isVariableDeclarationList(init)) {
    for (const decl of init.getDeclarations()) {
      const nameNode = decl.getNameNode();
      if (Node.isIdentifier(nameNode)) aliases.set(nameNode.getText(), nameNode.getText());
    }
  }
}

/**
 * Walk a single statement, descending into block/loop/conditional/try bodies
 * (emitting each action ONCE — never unrolling a loop or picking a branch) and
 * threading the alias map. Per-statement try/catch keeps one weird node from
 * sinking the parse.
 */
function walkStatement(
  stmt: Statement,
  aliases: AliasMap,
  sink: InteractionSink,
  ctx: WalkContext
): void {
  if (ctx.depth > MAX_WALK_DEPTH) {
    incrementUnclassified(sink, true);
    return;
  }
  const stmtCtx: StatementWalkContext = { ...ctx, statement: stmt };

  if (Node.isVariableStatement(stmt)) {
    let couldHide = false;
    for (const decl of stmt.getDeclarations()) {
      const init = decl.getInitializer();
      recordAlias(decl.getNameNode(), init, aliases);
      if (init) couldHide = extractActionsFromExpression(init, aliases, sink, stmtCtx) || couldHide; // catch Promise.all/await in init
    }
    incrementUnclassified(sink, couldHide);
    return;
  }

  if (Node.isExpressionStatement(stmt)) {
    const e = stmt.getExpression();
    let couldHide = false;
    // Reassignment: `x = page.locator(...)` (alias, last-write-wins).
    if (Node.isBinaryExpression(e) && e.getOperatorToken().getText() === '=') {
      const left = e.getLeft();
      recordAlias(Node.isIdentifier(left) ? left : undefined, e.getRight(), aliases);
      couldHide = extractActionsFromExpression(e.getRight(), aliases, sink, stmtCtx);
      incrementUnclassified(sink, couldHide);
      return;
    }
    couldHide = extractActionsFromExpression(e, aliases, sink, stmtCtx);
    incrementUnclassified(sink, couldHide);
    return;
  }

  if (Node.isReturnStatement(stmt)) {
    const expr = stmt.getExpression();
    if (expr && expressionHasControlFlowWithInteraction(expr, aliases)) markControlFlow(sink);
    incrementUnclassified(sink, expr ? expressionCouldHideInteraction(expr, aliases) : false);
    return;
  }

  if (Node.isBlock(stmt)) {
    walkStatements(stmt.getStatements(), aliases, sink, { ...ctx, depth: ctx.depth + 1 });
    return;
  }

  if (Node.isIfStatement(stmt)) {
    markControlFlow(sink);
    incrementUnclassified(sink, expressionCouldHideInteraction(stmt.getExpression(), aliases));
    const childCtx: WalkContext = { ...ctx, depth: ctx.depth + 1, underControlFlow: true };
    walkStatement(stmt.getThenStatement(), aliases, sink, childCtx);
    const els = stmt.getElseStatement();
    if (els) walkStatement(els, aliases, sink, childCtx);
    return;
  }

  if (Node.isForOfStatement(stmt) || Node.isForInStatement(stmt)) {
    markControlFlow(sink);
    recordLoopBinding(stmt.getInitializer(), aliases);
    walkStatement(stmt.getStatement(), aliases, sink, {
      ...ctx,
      depth: ctx.depth + 1,
      underControlFlow: true,
    });
    return;
  }

  if (Node.isForStatement(stmt) || Node.isWhileStatement(stmt) || Node.isDoStatement(stmt)) {
    markControlFlow(sink);
    walkStatement(stmt.getStatement(), aliases, sink, {
      ...ctx,
      depth: ctx.depth + 1,
      underControlFlow: true,
    });
    return;
  }

  if (Node.isTryStatement(stmt)) {
    markControlFlow(sink);
    const childCtx: WalkContext = { ...ctx, depth: ctx.depth + 1, underControlFlow: true };
    const tryBlock = stmt.getTryBlock();
    if (tryBlock) walkStatements(tryBlock.getStatements(), aliases, sink, childCtx);
    const catchClause = stmt.getCatchClause();
    if (catchClause)
      walkStatements(catchClause.getBlock().getStatements(), aliases, sink, childCtx);
    const finallyBlock = stmt.getFinallyBlock();
    if (finallyBlock) walkStatements(finallyBlock.getStatements(), aliases, sink, childCtx);
    return;
  }

  if (Node.isSwitchStatement(stmt)) {
    markControlFlow(sink);
    if (!sink.strict) return;
    incrementUnclassified(sink, expressionCouldHideInteraction(stmt.getExpression(), aliases));
    const childCtx: WalkContext = { ...ctx, depth: ctx.depth + 1, underControlFlow: true };
    for (const clause of stmt.getCaseBlock().getClauses()) {
      walkStatements(clause.getStatements(), aliases, sink, childCtx);
    }
    return;
  }

  // Function declarations / etc. → not descended.
}

/** Walk a list of statements in source order, each guarded so one can't sink the parse. */
function walkStatements(
  stmts: Statement[],
  aliases: AliasMap,
  sink: InteractionSink,
  ctx: WalkContext
): void {
  for (const stmt of stmts) {
    try {
      walkStatement(stmt, aliases, sink, ctx);
    } catch {
      // a single weird statement never sinks the whole parse
    }
  }
}

function fallbackParseResult(strict: false): RunCodeParseResult;
function fallbackParseResult(strict: true): RunCodeStrictParseResult;
function fallbackParseResult(strict: boolean): RunCodeParseResult | RunCodeStrictParseResult;
function fallbackParseResult(strict: boolean): RunCodeParseResult | RunCodeStrictParseResult {
  if (strict)
    return { interactions: [], fullFallback: true, hasControlFlow: false, unclassifiedCount: 0 };
  return { interactions: [], fullFallback: true };
}

function parseRunCodeInternal(code: string, strict: false): RunCodeParseResult;
function parseRunCodeInternal(code: string, strict: true): RunCodeStrictParseResult;
function parseRunCodeInternal(
  code: string,
  strict: boolean
): RunCodeParseResult | RunCodeStrictParseResult {
  // Bail on pathologically large bodies — ts-morph parses synchronously on the
  // event loop and observability isn't worth blocking on a giant block.
  if (code.length > MAX_PARSE_CODE_LENGTH) {
    return fallbackParseResult(strict);
  }

  // A unique filename + forget() keeps each parse isolated on the shared
  // (amortized) Project — no fixed-file overwrite hazard even if a caller ever
  // held an AST node reference across an await.
  const fileName = `__run_code_${_fileCounter++}__.ts`;
  let sourceFile: SourceFile;
  try {
    sourceFile = getProject().createSourceFile(fileName, code, { overwrite: true });
  } catch {
    return fallbackParseResult(strict);
  }

  try {
    const aliases: AliasMap = new Map();
    if (strict) {
      const stats: StrictParseStats = { hasControlFlow: false, unclassifiedCount: 0 };
      const sink: InteractionSink = { strict: true, interactions: [], stats };
      walkStatements(sourceFile.getStatements(), aliases, sink, {
        depth: 0,
        underControlFlow: false,
      });
      return {
        interactions: sink.interactions,
        fullFallback: sink.interactions.length === 0,
        hasControlFlow: stats.hasControlFlow,
        unclassifiedCount: stats.unclassifiedCount,
      };
    }

    const sink: InteractionSink = { strict: false, interactions: [] };
    walkStatements(sourceFile.getStatements(), aliases, sink, {
      depth: 0,
      underControlFlow: false,
    });
    return { interactions: sink.interactions, fullFallback: sink.interactions.length === 0 };
  } catch {
    // Defensive: walkStatements already guards per-statement, but never throw.
    return fallbackParseResult(strict);
  } finally {
    // Remove the file from the shared Project. `forget()` only drops node
    // wrappers — it leaves the source file in the in-memory filesystem, so a
    // long-lived process would accumulate one file per parsed run_code body.
    getProject().removeSourceFile(sourceFile);
  }
}

/**
 * Parse a `run_code` body into a best-effort interaction list. Never throws —
 * a parse failure (or a block with no recognized action) yields
 * `{ interactions: [], fullFallback: true }`.
 */
export function parseRunCode(code: string): RunCodeParseResult {
  return parseRunCodeInternal(code, false);
}

/**
 * Strict parser variant for deterministic cache linking. It preserves the
 * best-effort interaction list while exposing static uncertainty signals.
 */
export function parseRunCodeStrict(code: string): RunCodeStrictParseResult {
  return parseRunCodeInternal(code, true);
}
